// Evidencia en servidor: reserva, confirmación, galería, URLs firmadas y eliminación.
import { FunctionsHttpError } from '@supabase/supabase-js';
import { AppFailure, fromFunctionBody, toAppError } from './errors.ts';
import { supabase } from './supabase.ts';

export const BUCKET = 'order-evidence';
export const SIGNED_URL_SECONDS = 300;

export interface MediaFile {
  id: string;
  order_id: string;
  kind: 'image' | 'video';
  path: string;
  bytes: number;
  duration: number | null;
  status: 'uploading' | 'ready' | 'deleting';
  created_by: string;
  created_by_name: string;
  created_at: string;
  ready_at: string | null;
}

function fail(error: unknown): never {
  throw new AppFailure(toAppError(error));
}

export async function listMedia(orderId: string): Promise<MediaFile[]> {
  const { data, error } = await supabase
    .from('media_files')
    .select('id, order_id, kind, path, bytes, duration, status, created_by, created_by_name, created_at, ready_at')
    .eq('order_id', orderId)
    .order('created_at')
    .order('id');
  if (error) fail(error);
  return data as MediaFile[];
}

export async function reserveMedia(args: {
  orderId: string;
  mediaId: string;
  kind: 'image' | 'video';
  bytes: number;
  duration: number | null;
}): Promise<MediaFile> {
  const { data, error } = await supabase.rpc('reserve_media', {
    p_order_id: args.orderId,
    p_media_id: args.mediaId,
    p_kind: args.kind,
    p_bytes: args.bytes,
    ...(args.duration !== null ? { p_duration: args.duration } : {}),
  });
  if (error) fail(error);
  return data as unknown as MediaFile;
}

export async function finalizeMedia(mediaId: string): Promise<MediaFile> {
  const { data, error } = await supabase.rpc('finalize_media', { p_media_id: mediaId });
  if (error) fail(error);
  return data as unknown as MediaFile;
}

export interface DeletionRequest {
  id: string;
  media_id: string;
  status: 'pending' | 'complete';
}

export async function requestDeletion(mediaId: string, reason: string): Promise<DeletionRequest> {
  const { data, error } = await supabase.rpc('request_file_deletion', { p_media_id: mediaId, p_reason: reason });
  if (error) fail(error);
  return data as unknown as DeletionRequest;
}

/** Pide al servidor borrar el objeto físico ya (si falla, el worker reintenta). */
export async function deleteEvidenceNow(deletionId: string): Promise<boolean> {
  const { error } = await supabase.functions.invoke('delete-evidence', { body: { deletion_id: deletionId } });
  if (!error) return true;
  if (error instanceof FunctionsHttpError) {
    const body: unknown = await (error.context as Response).json().catch(() => null);
    const appError = fromFunctionBody(body);
    if (appError.code === 'permission') throw new AppFailure(appError);
  }
  return false;
}

export async function signedUrls(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_URL_SECONDS);
  if (error) fail(error);
  const result: Record<string, string> = {};
  for (const item of data) if (item.path && item.signedUrl) result[item.path] = item.signedUrl;
  return result;
}

export async function signedUrl(path: string, download?: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_SECONDS, download ? { download } : undefined);
  if (error) fail(error);
  return data.signedUrl;
}

export interface ArchivedFile {
  media_id: string;
  kind: 'image' | 'video';
  bytes: number;
  created_by_name: string;
  created_at: string;
  backed_up_at: string;
  status: 'pending' | 'complete';
}

/** Evidencia liberada tras un backup (D13); solo staff recibe datos. */
export async function getArchived(orderId: string): Promise<ArchivedFile[]> {
  const { data, error } = await supabase.rpc('get_archived_evidence', { p_order_id: orderId });
  if (error) fail(error);
  return (data ?? []) as unknown as ArchivedFile[];
}

export interface ReleaseSummary {
  orders: number;
  files: number;
  bytes: number;
  dry_run: boolean;
}

export async function releaseArchived(closedBefore: string, dryRun: boolean): Promise<ReleaseSummary> {
  const { data, error } = await supabase.rpc('release_archived_evidence', {
    p_closed_before: closedBefore,
    p_dry_run: dryRun,
  });
  if (error) fail(error);
  return data as unknown as ReleaseSummary;
}
